import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import NewAnalysisForm from '../components/NewAnalysisForm';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { analysisColumnConfig } from '@/app/PageSpeed/config/analysisColumnConfig';
import { useGetAnalysisHistoryQuery } from '@/app/PageSpeed/api/analysisApi';
import AnalysisSettingsDrawer from '../components/AnalysisSettingsDrawer';
import NoAnalysisData from '../components/NoAnalysisData';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import useDomain from '@/hooks/useDomain';
import type { ActiveAnalysisJob } from '@/app/PageSpeed/api/types/analysisApiTypes';
import HistoryPreload from '../components/HistoryPreload';

type AgGridWrapperProps = {
  height: string;
  columns: unknown[];
  data: unknown[];
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const HistoryAnalysis = () => {
  const { domain, domain_slug } = useDomain();
  const access = useToolAccess('analysis');
  const [activeAnalysis, setActiveAnalysis] =
    useState<ActiveAnalysisJob | null>(null);

  const {
    data: historyData,
    isError,
    error,
    refetch,
    isFetching
  } = useGetAnalysisHistoryQuery(undefined, {
    skip: access.isLoading || !access.haveAccess
  });

  const handleAnalysisStarted = useCallback((jobId: string, url?: string) => {
    setActiveAnalysis({ jobId, url });
  }, []);

  const columns = useMemo(
    () =>
      analysisColumnConfig(domain_slug, domain, {
        onAnalysisStarted: handleAnalysisStarted
      }),
    [domain_slug, domain, handleAnalysisStarted]
  );

  useEffect(() => {
    if (activeAnalysis || !historyData?.data?.length) return;

    const runningAnalysis = historyData.data.find(
      item => item.status === 2 || item.status === 4
    );
    const jobId = runningAnalysis?.task_id || runningAnalysis?._id;

    if (jobId) {
      setActiveAnalysis({
        jobId: String(jobId),
        url: runningAnalysis.url
      });
    }
  }, [activeAnalysis, historyData?.data]);

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار واکاوی" />;
  }

  if (isFetching && !historyData) {
    return <HistoryPreload />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }

  return (
    <>
      <NewAnalysisForm
        activeAnalysis={activeAnalysis}
        onActiveAnalysisChange={setActiveAnalysis}
      />
      <AnalysisSettingsDrawer />
      {!historyData?.data?.length && (
        <NoAnalysisData showForm={false} />
      )}
      <style>
        {`
        .for-style .ag-row-focus {
        z-index: 2;}`}
      </style>
      {historyData?.data?.length ? (
        <div className="for-style">
          <TypedAgGridWrapper
            columns={columns}
            height="500px"
            data={historyData?.data || []}
            otherOptions={{ suppressRowVirtualisation: false }}
          />
        </div>
      ) : null}
    </>
  );
};

export default HistoryAnalysis;
